/**
 * ComparisonProductsFetcher
 * Reads comparison products data from the JSON script tag generated by Liquid
 */

class ComparisonProductsFetcher {
  constructor() {
    this.products = [];
    this.dataScriptId = 'comparison-products-data';
  }

  /**
   * Load products data from the JSON script tag
   */
  loadProductsFromScript() {
    const dataScript = document.getElementById(this.dataScriptId);

    if (!dataScript) {
      console.error('❌ Script tag with id "comparison-products-data" not found');
      return [];
    }

    try {
      const data = JSON.parse(dataScript.textContent);

      this.products = data.products;

      // Log each variant's details
      this.products.forEach((item, index) => {

        const cd = item.comparison_data;
      });

      if (this.products.length === 0) {
        console.warn('⚠️ No variants found with comparison metafields');
      }

      return this.products;

    } catch (error) {
      console.error('❌ Error parsing comparison products JSON:', error);
      return [];
    }
  }

  /**
   * Get all products
   */
  getProducts() {
    if (this.products.length === 0) {
      this.loadProductsFromScript();
    }
    return this.products;
  }

  /**
   * Initialize
   */
  init() {
    const products = this.loadProductsFromScript();
    return products;
  }
}

// Render the comparison table
function renderComparisonTable() {
  const container = document.getElementById('comparison-table-container');
  if (!container) {
    console.warn('⚠️ comparison-table-container not found');
    return;
  }

  const fetcher = new ComparisonProductsFetcher();
  const allProducts = fetcher.init();

  // Get only first 2 products
  const products = allProducts.slice(0, 2);

  if (products.length === 0) {
    container.innerHTML = '<div class="no-products-message"><p>No products with comparison data found.</p></div>';
    return;
  }

  // Get benefits configuration from data attribute
  const synergyTitle = container.dataset.synergyTitle || 'Stack Synergy';
  let benefits = [];

  try {
    benefits = JSON.parse(container.dataset.benefits || '[]');
  } catch (error) {
    console.error('❌ Error parsing benefits configuration:', error);
  }

  // Build table HTML
  let html = '<table class="supplement-comparison"><thead><tr><th class="category"></th>';

  // Product headers
  products.forEach(product => {
    // Use product/variant data directly (not from metafield)
    const img = product.featured_image;
    const name = product.title;

    html += `
      <th class="product-col">
        <div>
          ${img ? `<img src="${img}" alt="${name}" loading="lazy">` : ''}
          <div class="product-info">
            <strong>${name}</strong>
          </div>
        </div>
      </th>
    `;
  });

  html += `<th class="synergy-col">${synergyTitle}</th></tr></thead><tbody>`;

  // Render each benefit row dynamically from blocks
  benefits.forEach(benefit => {
    const iconHtml = benefit.icon ? `<img src="${benefit.icon}" alt="${benefit.label}" class="benefit-icon" loading="lazy"> ` : '';
    html += `<tr><td class="category-name">${iconHtml}${benefit.label}</td>`;
    products.forEach(p => {
      const benefitValue = p.comparison_data[benefit.benefit_key] || '—';
      html += `<td>${benefitValue}</td>`;
    });
    html += `<td>${benefit.synergy_value}</td></tr>`;
  });

  html += '</tbody></table>';
  container.innerHTML = html;
}

/**
 * Fetch product metafields from Shopify API
 */
async function fetchProductMetafields(productId) {

  try {
    const response = await fetch(`/products/${productId}.js`);
    const productData = await response.json();

    // Check if product has comparison metafield
    if (productData.metafields && productData.metafields.custom && productData.metafields.custom.product_comparison) {
      return {
        id: productData.id,
        title: productData.title,
        handle: productData.handle,
        featured_image: productData.featured_image,
        comparison_data: productData.metafields.custom.product_comparison
      };
    }

    return null;
  } catch (error) {
    console.error('❌ Error fetching product metafields:', error);
    return null;
  }
}

/**
 * Render comparison table with bundle-selected products
 */
async function renderComparisonTableFromBundle(bundleSelections) {

  const container = document.getElementById('comparison-table-container');
  if (!container) {
    console.warn('⚠️ comparison-table-container not found');
    return;
  }

  // Get selected variants from bundle
  const selectionValues = Object.values(bundleSelections).filter(Boolean);

  // Find the parent section wrapper to hide/show
  const sectionWrapper = container.closest('.section-wrapper');

  if (selectionValues.length === 0) {
    if (sectionWrapper) {
      sectionWrapper.style.display = 'none';
    }
    return;
  }

  // Get all variants with metafields from the JSON data
  const dataScript = document.getElementById('comparison-products-data');
  let allVariantsWithMetafields = [];

  if (dataScript) {
    try {
      const data = JSON.parse(dataScript.textContent);
      allVariantsWithMetafields = data.products || [];
    } catch (error) {
      console.error('❌ Error parsing comparison products JSON:', error);
    }
  }

  // Match bundle selections with variants that have metafields
  const productsToDisplay = [];

  for (const selection of selectionValues) {
    // Find variant in metafields data by variant_id
    const variantWithMetafield = allVariantsWithMetafields.find(item => {
      // Match by variant_id (most accurate)
      if (item.variant_id && selection.variantId) {
        const variantIdMatch = String(item.variant_id) === String(selection.variantId);
        return variantIdMatch;
      }

      // Fallback: match by handle AND variant title
      const handleMatch = item.handle === selection.productHandle;
      const variantMatch = item.variant_title === selection.variantTitle;
      const matches = handleMatch && variantMatch;
      return matches;
    });

    if (variantWithMetafield) {

      productsToDisplay.push({
        ...variantWithMetafield,
        bundleSelection: selection // Keep original selection data
      });
    } else {
      // Still add product but without comparison data
      productsToDisplay.push({
        id: selection.productId,
        variant_id: selection.variantId,
        title: selection.productTitle,
        variant_title: selection.variantTitle,
        handle: selection.productHandle,
        comparison_data: null,
        bundleSelection: selection
      });
    }
  }

  // If no variants with metafields found, hide the section
  if (productsToDisplay.every(p => !p.comparison_data)) {
    if (sectionWrapper) {
      sectionWrapper.style.display = 'none';
    }
    return;
  }

  // Show the section if we have data to display
  if (sectionWrapper) {
    sectionWrapper.style.display = 'block';
  }

  // Render the comparison table
  const synergyTitle = container.dataset.synergyTitle || 'Stack Synergy';
  let benefits = [];

  try {
    benefits = JSON.parse(container.dataset.benefits || '[]');
  } catch (error) {
    console.error('❌ Error parsing benefits configuration:', error);
  }

  // Limit to first 2 products
  const products = productsToDisplay.slice(0, 2);

  let html = '<table class="supplement-comparison"><thead><tr><th class="category"></th>';

  // Product headers
  products.forEach(product => {
    // Use variant image and data directly from bundle selection
    const bundleSelection = product.bundleSelection;

    let img, name, variantInfo;

    if (bundleSelection) {
      // Use data from the bundle selection (variant specific)
      img = bundleSelection.variantImage || product.featured_image;
      name = bundleSelection.productTitle;
      variantInfo = bundleSelection.variantTitle;
    } else {
      // Fallback to product/variant data
      img = product.featured_image;
      name = product.title;
      variantInfo = product.variant_title || '';
    }

    html += `
      <th class="product-col">
        <div>
          ${img ? `<img src="${img}" alt="${name}" loading="lazy">` : ''}
          <div class="product-info">
            <strong>${name}</strong>
          </div>
        </div>
      </th>
    `;
  });

  html += `<th class="synergy-col">${synergyTitle}</th></tr></thead><tbody>`;

  // Render each benefit row dynamically from blocks
  benefits.forEach(benefit => {
    const iconHtml = benefit.icon ? `<img src="${benefit.icon}" alt="${benefit.label}" class="benefit-icon" loading="lazy"> ` : '';
    html += `<tr><td class="category-name">${iconHtml}${benefit.label}</td>`;
    products.forEach(p => {
      const benefitValue = p.comparison_data?.[benefit.benefit_key] || '—';
      const isEmpty = benefitValue === '—' || benefitValue === '-' || !benefitValue;
      const cellClass = isEmpty ? 'empty-cell' : '';
      html += `<td class="${cellClass}">${benefitValue}</td>`;
    });
    html += `<td>${benefit.synergy_value}</td></tr>`;
  });

  html += '</tbody></table>';
  container.innerHTML = html;
}

/**
 * Render comparison table with first variants from bundle dropdowns
 * This provides initial content before user makes selections
 */
function renderInitialComparisonTableForBundle() {
  const dropdowns = document.querySelectorAll('.bundle-dropdown');

  if (!dropdowns.length) {
    console.warn('⚠️ No bundle dropdowns found for initial rendering');
    return;
  }

  const initialSelections = {};

  dropdowns.forEach(dropdown => {
    const componentKey = dropdown.dataset.componentKey;
    const componentTitle = dropdown.dataset.componentTitle || '';
    const defaultQuantityRaw = parseInt(dropdown.dataset.defaultQuantity, 10);
    const defaultQuantity = Number.isNaN(defaultQuantityRaw) ? 1 : defaultQuantityRaw;
    const firstItem = dropdown.querySelector('.bundle-item');

    if (firstItem && componentKey) {
      // Extract data from first item's data attributes
      const productId = firstItem.dataset.productId;
      const productHandle = firstItem.dataset.productHandle;
      const variantId = firstItem.dataset.variantId;
      const productTitle = firstItem.dataset.productTitle;
      const variantTitle = firstItem.dataset.variantTitle;
      const imageSrc = firstItem.dataset.image;
      const priceCents = firstItem.dataset.price;
      const compareAtPriceCents = firstItem.dataset.compareAtPrice;

      // Build selection object matching bundles.js structure
      initialSelections[componentKey] = {
        productId,
        productHandle,
        variantId,
        productTitle,
        variantTitle,
        variantImage: imageSrc,
        componentKey: componentKey,
        quantity: defaultQuantity,
        componentTitle,
        price: priceCents,
        compareAtPrice: compareAtPriceCents
      };
    }
  });

  // Only render if we have selections
  if (Object.keys(initialSelections).length > 0) {
    renderComparisonTableFromBundle(initialSelections);
  }
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {

  // Check if this is a bundle product page
  const isBundlePage = Boolean(document.querySelector('.bundle-container'));
  const container = document.getElementById('comparison-table-container');
  const sectionWrapper = container?.closest('.section-wrapper');

  if (isBundlePage) {
    // On bundle pages, show the section and render with first variants
    if (sectionWrapper) {
      sectionWrapper.style.display = 'block';
    }

    // Render table with first variant of each bundle component
    renderInitialComparisonTableForBundle();
  } else {
    // On non-bundle pages, render the table with default JSON data
    renderComparisonTable();
  }

  // Also make fetcher globally accessible for debugging
  const fetcher = new ComparisonProductsFetcher();
  window.comparisonFetcher = fetcher;

  // Listen for bundle selection changes
  document.addEventListener('bundle:selection-changed', (event) => {
    // Render table with bundle selections
    renderComparisonTableFromBundle(event.detail.selections);
  });
});
